import { useAuthState } from "@/contexts/auth/auth.provider";
import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import {
  CREATE_EJARI_RNPL_REQUEST,
  CREATE_HARAJ_EJARI_RNPL_REQUEST,
} from "@/graphql/mutations/listing";
import { SuccessEjari } from "@/components/rnpl/SuccessEjari";
import css from "@/src/pages/haraj/rnpl/[haraj_listing_id]/index.module.scss";
import { Button, Checkbox, Input, Modal } from "@aqar-app/react-ui";
import FieldError from "@/components/field-error/FieldError";
import { DatePicker } from "@aqar-app/aqar-design-system";
import momentHijri from "moment-hijri";
import { writeHijri } from "@/lib/ejarContractData";
import moment from "moment/moment";
import dayjs from "dayjs";
import { RNPL_PROVIDERS } from "@/common/constants";
import { useRouter } from "next/router";
import { trackRNPLProviderConfirm } from "@/lib/tracking/trackRNPLEvents";
import { usePath } from "@/contexts/path/path";
import { ClassicModal } from "@/components/Listing/ListingScreen/classicModal";
import { sharedHarajRNPLTracker } from "@/lib/tracking/trackHarajRNPLEvents";

moment.locale("ar");

function validateId(id) {
  const permitRegex = /^([12])([0-9]{9})$/;
  return permitRegex.test(id);
}

export const HarajEjariForm = ({ adLicenseNumber }) => {
  const router = useRouter();
  const { token } = useAuthState();
  const { previousPath } = usePath();
  const [values, setValues] = useState({
    acceptTerms: false,
    acceptCheck: false,
    userId: "",
    userBirthDate: "",
  });
  const [idError, setIdError] = useState(false);
  const [calenderMode, setCalenderMode] = useState("georgian");
  const [isHijri, setIsHijri] = useState(false);

  const [createEjariRequest, { loading, data }] = useMutation(
    CREATE_HARAJ_EJARI_RNPL_REQUEST,
    {
      context: { token },
      variables: {
        ad_license_number: adLicenseNumber,
        id_number: values.userId,
        date_of_birth: values.userBirthDate,
      },
      onCompleted: (data) => {
        const requestData = data?.RentNowPayLater?.createEjariRnplRequest;
        router.push(
          `/haraj/rnpl/summary/${requestData?.id}?provider=${RNPL_PROVIDERS.ejari.name}&request_id=${requestData?.request_number}&request_date=${requestData?.timestamp}&listing_id=${listingId}&monthly_payment=${requestData?.monthly_payment}&max_monthly_payment=${requestData?.max_monthly_payment}`,
        );
      },
      onError: (error) => {
        Modal.open(
          <ClassicModal
            ok={{
              label: "حسنا",
              cb: (e) => Modal.close(e),
            }}
            body={<p>{error.message || "الرجاء المحاولة لاحقاً."}</p>}
          />,
        )();
      },
    },
  );

  const isFormValid = () => {
    return (
      values.acceptTerms &&
      values.acceptCheck &&
      values.userBirthDate &&
      values.userId &&
      !idError &&
      validateId(values.userId)
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isFormValid()) {
      sharedHarajRNPLTracker({
        target: "ejari_confirm",
        source: previousPath || decodeURI(document.referrer),
      });
      createEjariRequest();
    }
  };

  return (
    <div className={css.providerForm}>
      <div className={css.ejari}>
        <div className={css.fieldGroup}>
          <label>رقم الهوية *</label>
          <Input
            value={values.userId}
            placeholder="أدخل هنا..."
            type="number"
            dir="RTL"
            onChange={(e) => {
              const { value } = e.target;
              if (value.length > 10) {
                return;
              }
              setValues({ ...values, userId: value });
              setIdError(!validateId(value));
            }}
          />
          {idError && <FieldError text="رقم الهوية المدخل غير صحيح" />}
        </div>
        <div className={css.fieldGroup + " " + css.datePicker}>
          <div className={css.datePickerHeader}>
            <label>تاريخ الميلاد *</label>
            <Checkbox
              size="small"
              checked={calenderMode === "hijri"}
              onChange={(e) => {
                setIsHijri(e.target.checked);
                setCalenderMode(e.target.checked ? "hijri" : "gregorian");
              }}
            >
              هجري
            </Checkbox>
          </div>
          {isHijri ? (
            <DatePicker
              locale="ar"
              value={values.userBirthDate}
              openToDate={momentHijri(values.userBirthDate || "1993-09-28")}
              calenderMode="hijri"
              showMonthYearDropdown={true}
              showMonthDropdown={false}
              showYearDropdown={false}
              scrollableYearDropdown={true}
              minDate={momentHijri().subtract(80, "years")}
              maxDate={momentHijri().subtract(18, "years")}
              onSelect={(val) => {
                const txtValue = writeHijri(new Date(val));
                setValues({ ...values, userBirthDate: txtValue });
              }}
              hidePlaceholder
            />
          ) : (
            <DatePicker
              locale="ar"
              openToDate={moment(values.userBirthDate || "1993-09-28")}
              minDate={moment().subtract(80, "years")}
              maxDate={moment().subtract(18, "years")}
              onSelect={(val) => {
                const txtValue = dayjs(val).format("YYYY-MM-DD");
                setValues({ ...values, userBirthDate: txtValue });
              }}
              hidePlaceholder
            />
          )}
        </div>
        <Checkbox
          checked={values.acceptTerms}
          onChange={(e) =>
            setValues({ ...values, acceptTerms: e.target.checked })
          }
          size="small"
        >
          <p className={css.terms}>
            أوافق على شروط الخدمة وأقر بحصولي على موافقة المعلن على تأجير الوحدة
            بالباطن. وأوافق على{" "}
            <a href={RNPL_PROVIDERS.ejari.ar_tc} target="_blank">
              الشروط والأحكام
            </a>{" "}
            و
            <a href={RNPL_PROVIDERS.ejari.ar_pp} target="_blank">
              سياسة الخصوصية
            </a>{" "}
            الخاصة بمزود الخدمة.
          </p>
        </Checkbox>
        <Checkbox
          checked={values.acceptCheck}
          onChange={(e) =>
            setValues({ ...values, acceptCheck: e.target.checked })
          }
          size="small"
        >
          <p className={css.terms}>
            أوافق على أن يقوم مزود الخدمة بالاستعلام عن سجلي الائتماني عن طريق
            منصة سمة.
          </p>
        </Checkbox>
        <div className={css.submitBtn}>
          <Button
            theme="primary"
            outline
            disabled={!isFormValid() || loading}
            onClick={handleSubmit}
          >
            إرسال
          </Button>
        </div>
      </div>
    </div>
  );
};
